// M5.12: peer-to-peer coin transfer modal.
//
// Player picks a teammate, enters an amount (1-100), and
// optionally a short note. Posts to /api/me/coins/transfer.
// On success, the modal shows the new balance and closes;
// the coin history modal (which the player opened this
// from) refetches on next open.
//
// Atomicity: the api route does the pessimistic-lock
// transaction. We don't double-check the balance on the
// web side — the api is authoritative. We pass the
// user's current balance into the disabled-when-over
// button, but the api is the source of truth on submit.
//
// Why no fee, no reversal: ADR says "transfer is final;
// admin can void like any coin row". The transfer note
// shows up on both the sender's and receiver's coin
// history as "transfer to/from <name>: <note>".
//
// Why email not user_id: the picker uses the same /api/users
// list the admin users page uses, filtered to non-self +
// non-ignored. The api endpoint accepts either; we send
// email because the picker renders by display name.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import type { CurrentUser, User } from '../../lib/types';
import { transferCoins } from './api';

export function TransferCoinsModal({
  me,
  balance,
  onClose,
}: {
  me: CurrentUser;
  balance: number;
  onClose: () => void;
}) {
  // ESC closes. Standard dialog UX; matches the
  // GrantXpCoinsModal + CoinHistoryModal patterns.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [search, setSearch] = useState('');
  const [pick, setPick] = useState<User | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  // Pull all users, then filter client-side. ~20 users
  // today; would need a server-side ?search= filter if
  // the team grows past a few hundred.
  const users = useQuery<{ users: User[] }>({
    queryKey: ['users'],
    queryFn: () => api<{ users: User[] }>('/api/users'),
  });

  // Filter to non-self, non-ignored, name-or-email
  // matches the search string. Empty search shows
  // nothing (forces a deliberate pick instead of
  // an accidental click on the first row).
  const candidates = useMemo(() => {
    if (!users.data) return [];
    const q = search.trim().toLowerCase();
    return users.data.users.filter(
      (u) =>
        u.id !== me.id &&
        !u.is_ignored &&
        (q === '' ||
          u.display_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.handle.toLowerCase().includes(q)),
    );
  }, [users.data, search, me.id]);

  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () =>
      transferCoins(
        pick!.email,
        Number(amount),
        note.trim() || undefined,
      ),
    onSuccess: () => {
      // Refresh both the coin balance (for the chip)
      // and the coin history (for the modal that
      // opened this).
      void qc.invalidateQueries({ queryKey: ['me-coins'] });
      void qc.invalidateQueries({ queryKey: ['me-coin-transactions'] });
      onClose();
    },
  });

  const amountNum = Number(amount);
  const valid = pick && Number.isInteger(amountNum) && amountNum >= 1 && amountNum <= 100 && amountNum <= balance;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-title"
      onClick={onClose}
    >
      <div
        className="card flex max-h-[80vh] w-full max-w-lg flex-col p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-white/5 p-4">
          <h2 id="transfer-title" className="text-lg font-semibold">
            Send coins
          </h2>
          <button type="button" onClick={onClose} className="btn-ghost" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <p className="mb-3 text-sm text-ink-muted">
            Send <span className="font-mono text-ink">1-100</span> coins to a teammate. Balance:{' '}
            <span className="font-mono text-ink">{balance}</span> 🪙. The transfer is final.
          </p>

          {!pick ? (
            <div>
              <label className="mb-1 block text-xs text-ink-muted">Recipient</label>
              <input
                type="search"
                placeholder="Search by name, handle, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2 w-full rounded bg-bg-elevated px-2 py-1 text-sm"
                autoFocus
              />
              {users.isPending ? (
                <p className="text-sm text-ink-muted">Loading…</p>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-ink-muted">No matching teammates.</p>
              ) : (
                <ul className="max-h-64 overflow-y-auto rounded border border-white/5">
                  {candidates.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => setPick(u)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg-elevated"
                      >
                        <span>
                          <span className="text-ink">{u.display_name}</span>{' '}
                          <span className="text-ink-muted">@{u.handle}</span>
                        </span>
                        <span className="text-xs text-ink-dim">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (valid) mut.mutate();
              }}
            >
              <div className="mb-3 flex items-center justify-between rounded bg-bg-elevated/60 px-3 py-2">
                <div>
                  <div className="text-sm">
                    <span className="text-ink">{pick.display_name}</span>{' '}
                    <span className="text-ink-muted">@{pick.handle}</span>
                  </div>
                  <div className="text-xs text-ink-dim">{pick.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setPick(null)}
                  className="btn-ghost text-xs"
                >
                  Change
                </button>
              </div>

              <label className="mb-1 block text-xs text-ink-muted">Amount</label>
              <input
                type="number"
                min="1"
                max={Math.min(100, balance)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mb-1 w-full rounded bg-bg-elevated px-2 py-1 text-sm tabular-nums"
                autoFocus
              />
              {amount !== '' && amountNum > balance ? (
                <p className="mb-2 text-xs text-err">
                  Not enough coins. You have {balance}.
                </p>
              ) : amount !== '' && amountNum > 100 ? (
                <p className="mb-2 text-xs text-err">Max 100 per transfer.</p>
              ) : (
                <p className="mb-2 text-xs text-ink-dim">1-100 coins.</p>
              )}

              <label className="mb-1 block text-xs text-ink-muted">Note (optional)</label>
              <input
                type="text"
                maxLength={200}
                placeholder="e.g. thanks for the review"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mb-3 w-full rounded bg-bg-elevated px-2 py-1 text-sm"
              />

              {mut.isError ? (
                <p className="mb-2 text-xs text-err">{describeError(mut.error)}</p>
              ) : null}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="btn-ghost">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!valid || mut.isPending}
                  className="btn-primary disabled:opacity-50"
                >
                  {mut.isPending ? 'Sending…' : `Send ${amount || 0} 🪙`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.body && (err.body as { error?: string }).error) {
      case 'insufficient_balance':
        return 'Not enough coins.';
      case 'user_not_found':
        return 'Recipient not found.';
      case 'user_ignored':
        return 'Recipient is ignored.';
      case 'cannot_transfer_to_self':
        return 'You cannot send coins to yourself.';
      case 'invalid_body':
        return 'Invalid amount or note.';
      default:
        return err.message;
    }
  }
  return 'Transfer failed.';
}
